/**
 * Carbon text area: the field frame around a native <textarea>, 4 rows by
 * default, resizable vertically only. Same field fill, underline, focus and
 * Coral validation outline as TextInput.
 */
import { forwardRef, useId, type TextareaHTMLAttributes } from 'react'
import clsx from 'clsx'
import { FormField } from './FormField'
import { FIELD_BASE, FIELD_INVALID, fieldIds } from './fieldStyles'
import type { FieldChromeProps } from './TextInput'

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, Omit<FieldChromeProps, 'size'> {
  wrapperClassName?: string
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, helper, error, hideLabel, id: idProp, className, wrapperClassName, rows = 4, disabled, ...rest },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const { describedBy } = fieldIds(id, helper, error)
  return (
    <FormField id={id} label={label} helper={helper} error={error} hideLabel={hideLabel} disabled={disabled} className={wrapperClassName}>
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={clsx(FIELD_BASE, 'min-h-20 resize-y py-[11px] leading-[1.43]', error && FIELD_INVALID, className)}
        {...rest}
      />
    </FormField>
  )
})

export default TextArea
